import React, { useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  formatCompactNumber,
  formatMoney,
  formatNumber,
  formatPercent,
  humanizeTraceName,
  shareOf,
} from "../format";
import { internalOrgReason, isInternalOrg } from "../internalOrgs";

/**
 * Client-first view of AI spend: who costs the most, and — on expand — WHY,
 * via their feature mix and how their per-run economics compare to the
 * platform average.
 *
 * `featureMatrix` is the organizationId x traceName breakdown, so the
 * expansion needs no extra request.
 */
export function ClientBreakdown({
  organizations,
  featureMatrix,
  orgNames,
  excludeInternal,
  totalCost,
}) {
  const [expanded, setExpanded] = useState(null);

  const { rows, excludedCost, excludedCount } = useMemo(() => {
    let cost = 0;
    let count = 0;
    const kept = [];
    (organizations || []).forEach((org) => {
      if (excludeInternal && isInternalOrg(org.key, orgNames[org.key])) {
        cost += org.totalCost || 0;
        count += 1;
        return;
      }
      kept.push(org);
    });
    return {
      rows: kept.sort((a, b) => (b.totalCost || 0) - (a.totalCost || 0)),
      excludedCost: cost,
      excludedCount: count,
    };
  }, [organizations, orgNames, excludeInternal]);

  // orgId -> its per-feature rows
  const featuresByOrg = useMemo(() => {
    const map = {};
    (featureMatrix || []).forEach((row) => {
      const key = row.primaryKey;
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(row);
    });
    Object.values(map).forEach((list) =>
      list.sort((a, b) => (b.totalCost || 0) - (a.totalCost || 0)),
    );
    return map;
  }, [featureMatrix]);

  const visibleTotal = rows.reduce((sum, r) => sum + (r.totalCost || 0), 0);

  // Platform-wide average cost per run, so a client's own average can be shown
  // as "1.8x the average" — the quickest signal of an anomalous customer.
  const platformAvg = useMemo(() => {
    const runs = rows.reduce((sum, r) => sum + (r.traceCount || 0), 0);
    return runs > 0 ? visibleTotal / runs : 0;
  }, [rows, visibleTotal]);

  if (!rows.length) {
    return (
      <p className="co-empty">
        No client-attributed AI spend in this window.
        {excludedCount > 0
          ? ` ${excludedCount} Addy-internal org(s) were excluded.`
          : ""}
      </p>
    );
  }

  return (
    <>
      {excludeInternal && excludedCount > 0 ? (
        <div className="co-overhead-banner co-overhead-banner--neutral">
          <span>
            Excluding {excludedCount} Addy-internal org(s) worth{" "}
            <strong>{formatMoney(excludedCost)}</strong> (
            {formatPercent(shareOf(excludedCost, totalCost))} of all AI spend).
            Detection is name + ID based only — see internalOrgs.js.
          </span>
        </div>
      ) : null}

      <div className="ou-table-wrap">
        <table className="ou-table">
          <thead>
            <tr>
              <th style={{ width: 28 }} />
              <th>Client</th>
              <th>Cost</th>
              <th>Share</th>
              <th>Runs</th>
              <th>Avg $/run</th>
              <th>vs avg</th>
              <th>Tokens</th>
              <th>Features</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 50).map((org) => {
              const isOpen = expanded === org.key;
              const name = orgNames[org.key];
              const reason = internalOrgReason(org.key, name);
              const runs = org.traceCount || 0;
              const avg = runs ? (org.totalCost || 0) / runs : 0;
              const ratio = platformAvg > 0 ? avg / platformAvg : 0;
              const orgFeatures = featuresByOrg[org.key] || [];

              return (
                <React.Fragment key={org.key || "unattributed"}>
                  <tr
                    className="is-clickable"
                    onClick={() => setExpanded(isOpen ? null : org.key)}
                  >
                    <td>
                      {isOpen ? (
                        <ExpandMoreIcon fontSize="small" />
                      ) : (
                        <ChevronRightIcon fontSize="small" />
                      )}
                    </td>
                    <td title={org.key}>
                      {name || org.label || org.key || "Unattributed"}
                      {reason ? (
                        <span className="co-internal-tag" title={reason}>
                          internal
                        </span>
                      ) : null}
                    </td>
                    <td className="co-strong">{formatMoney(org.totalCost)}</td>
                    <td>
                      <div className="co-share">
                        <div
                          className="co-share-bar"
                          style={{
                            width: `${Math.min(shareOf(org.totalCost, visibleTotal) * 100, 100)}%`,
                          }}
                        />
                        <span>
                          {formatPercent(shareOf(org.totalCost, visibleTotal))}
                        </span>
                      </div>
                    </td>
                    <td>{formatNumber(runs)}</td>
                    <td>{runs ? formatMoney(avg) : "—"}</td>
                    <td className={ratio >= 2 ? "co-err" : ""}>
                      {ratio ? `${ratio.toFixed(1)}x` : "—"}
                    </td>
                    <td>{formatCompactNumber(org.totalTokens)}</td>
                    <td>{formatNumber(orgFeatures.length || null)}</td>
                  </tr>

                  {isOpen ? (
                    <tr className="co-detail-row">
                      <td colSpan={9}>
                        {orgFeatures.length ? (
                          <>
                            <p className="co-note">
                              What this client spends on — their feature mix is
                              the "why" behind the number above.
                            </p>
                            <table className="ou-table co-inner-table">
                              <thead>
                                <tr>
                                  <th>Feature / step</th>
                                  <th>Cost</th>
                                  <th>Share of client</th>
                                  <th>Runs</th>
                                  <th>Avg $/run</th>
                                  <th>Tokens</th>
                                </tr>
                              </thead>
                              <tbody>
                                {orgFeatures.map((f) => {
                                  const fRuns = f.traceCount || 0;
                                  return (
                                    <tr key={f.secondaryKey}>
                                      <td title={f.secondaryKey}>
                                        {humanizeTraceName(f.secondaryKey)}
                                      </td>
                                      <td>{formatMoney(f.totalCost)}</td>
                                      <td>
                                        {formatPercent(
                                          shareOf(f.totalCost, org.totalCost),
                                        )}
                                      </td>
                                      <td>{formatNumber(fRuns)}</td>
                                      <td>
                                        {fRuns
                                          ? formatMoney((f.totalCost || 0) / fRuns)
                                          : "—"}
                                      </td>
                                      <td>{formatCompactNumber(f.totalTokens)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </>
                        ) : (
                          <p className="co-empty">
                            No per-feature breakdown returned for this client —
                            it likely fell outside the row limit.
                          </p>
                        )}
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length > 50 ? (
        <p className="co-note">Showing top 50 of {rows.length} clients.</p>
      ) : null}
    </>
  );
}
